import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';

import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Autocomplete from '../shared/Autocomplete';
import ConfirmationModal from '../modals/ConfirmationModal';
import SelectBox from '../shared/select-box/SelectBox';
import SelectBoxList from '../shared/select-box/SelectBoxList';

import userRoles from '../../constants/userRoles';

const DistributorsModal = (props) => {
  const {
    mode = 'create',
    open,
    onClose,
    onSubmit,
    loading,
    // countries = [],
    proceduresList = [],
    companies = [],
    currentUser,
    initialValues,
  } = props;

  const [view, setView] = useState(0);
  const [name, setName] = useState('');
  const [company, setCompany] = useState(null);
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [postCode, setPostCode] = useState('');
  const [country, setCountry] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [search, setSearch] = useState('');

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmResolver] = useState(null);
  const [optionToRemove, setOptionToRemove] = useState(null);

  useEffect(() => {
    if (open && mode === 'create' && currentUser?.role === userRoles.COUNTRY_ADMIN.name) {
      setCompany(companies?.find((c) => c.id === currentUser.company));
    }
    if (open && mode === 'update') {
      setName(initialValues?.name || '');
      setCompany(initialValues?.company ? companies?.find((c) => c.id === initialValues.company) : null);
      setAddress(initialValues?.address || '');
      setCountry(initialValues?.country || null);
      setState(initialValues?.state || '');
      setPostCode(initialValues?.postCode || '');
      setProcedures(initialValues?.assignedProcedures || []);
    }
  }, [open, mode, initialValues, proceduresList, companies, currentUser]);

  const resetValues = () => {
    setName('');
    setCompany(null);
    setCountry(null);
    setAddress( '');
    setState('');
    setPostCode('');
    setProcedures([]);
    setView(0);
  };

  const handleSubmit = async () => {
    const formObj = {
      name: name || '',
      company: company?.id || null,
      address,
      country,
      state,
      postCode,
      assignedProcedures: procedures,
    };
    await onSubmit(formObj);
    resetValues();
  };

  const isDisabled = () => {
    return !name || !company || !country;
  };

  // const handleConfirmRemove = (option) => {
  //   return new Promise((resolve) => {
  //     setOptionToRemove(option);
  //     setConfirmOpen(true);
  //     setConfirmResolver(() => resolve);
  //   });
  // };

  const handleConfirmSubmit = () => {
    confirmResolver?.(true);
    setConfirmOpen(false);
    setOptionToRemove(null);
  };

  const handleConfirmClose = () => {
    confirmResolver?.(false);
    setConfirmOpen(false);
    setOptionToRemove(null);
  };

  const onProcedureSelect = (value) => {
    if (value && !procedures?.find((p) => p.id === value?.id)) {
      setProcedures([...procedures, value]);
    }
  };

  const onProcedureRemove = (id) => {
    setProcedures([...procedures].filter((p) => p.id !== id));
    setSearch('');
  }

  const proceduresOptions = useMemo(() => {
    return proceduresList.filter((p) => {
      let persistence = false;

      if (currentUser.role === userRoles.GLOBAL_ADMIN.name) {
        persistence = true;
      }

      if (currentUser.role === userRoles.COUNTRY_ADMIN.name) {
        persistence = p?.active?.find((c) => currentUser.administrationCountries.includes(c));
      }

      return persistence && !procedures?.find((procedure) => procedure?.id === p?.id);
    });
  }, [proceduresList, procedures, currentUser]);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      title={mode === 'update' ? 'Edit Distributor' : 'Create Distributor'}
      subtitle="Enter distributor details"
      submitText={mode === 'update' ? 'Save Changes' : 'Create'}
      disabled={isDisabled()}
      loading={loading}
      onSubmit={handleSubmit}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' } }}>
        {view === 0 ? (
          <Grid container spacing={1} sx={{ maxWidth: '560px' }}>
            <Grid size={{ xs: 12 }}>
              <Input
                id="name"
                type="text"
                placeholder=""
                value={name}
                onChange={(e) => setName(e.target.value)}
                label="Distributor Name"
                required
              />
            </Grid>
            {currentUser?.role === userRoles.GLOBAL_ADMIN.name && (
              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  value={company}
                  onChange={(value) => {
                    setCompany(value);
                    setCountry(null);
                  }}
                  getOptionLabel={(option) => option.name || ''}
                  options={companies}
                  label="Company"
                  required
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                value={country}
                onChange={setCountry}
                options={company?.countries?.filter((country) => currentUser?.role === userRoles.GLOBAL_ADMIN.name || currentUser?.administrationCountries?.includes(country)) || []}
                label="Country"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Input
                id="address"
                type="text"
                placeholder=""
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                label="Address"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                id="state"
                type="text"
                placeholder=""
                value={state}
                onChange={(e) => setState(e.target.value)}
                label="State"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                id="postCode"
                type="text"
                placeholder=""
                value={postCode}
                onChange={(e) => setPostCode(e.target.value)}
                label="Post Code"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              {/*<Autocomplete*/}
              {/*  multiple*/}
              {/*  value={procedures}*/}
              {/*  onChange={setProcedures}*/}
              {/*  confirmOnRemove={mode === 'update'}*/}
              {/*  onConfirmRemove={handleConfirmRemove}*/}
              {/*  getOptionLabel={(option) => option.name || ''}*/}
              {/*  options={proceduresList.map((p) => ({*/}
              {/*    id: p.id,*/}
              {/*    name: p.name,*/}
              {/*  }))}*/}
              {/*  label="Procedures"*/}
              {/*/>*/}

              <SelectBox
                label="Procedures"
                selected={procedures?.map((p) => ({ key: p?.id, label: p?.name }))}
                onClick={() => setView(1)}
              />
            </Grid>
          </Grid>
        ) : (
          <SelectBoxList
            sx={{ maxWidth: '560px' }}
            title="Select Procedures"
            onBackClick={() => setView(0)}
            search={search}
            setSearch={setSearch}
            onChange={(selected) => {
              onProcedureSelect(selected);
              setSearch('');
            }}
            searchLabel="Add Procedure"
            options={proceduresOptions}
            selected={sortBy(procedures, 'name')}
            onRemoveItem={onProcedureRemove}
            getOptionLabel={(option) => `${option.name}${option.description ? ` – ${option.description}` : ''}`}
          />
        )}
      </Box>

      {mode === 'update' && (
        <ConfirmationModal
          open={confirmOpen}
          onClose={handleConfirmClose}
          onSubmit={handleConfirmSubmit}
          title="Remove Procedure?"
          text={`Are you sure you want to remove "${optionToRemove?.name}" from this distributor?`}
          variant="warning"
          submitText="Remove"
          closeText="Cancel"
        />
      )}
    </Drawer>
  );
};

DistributorsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default DistributorsModal;
